import React, { useState, useEffect, useCallback } from "react";
import searchIcon from "../../assets/icons/search.svg";

interface SearchProps {
  search: (searchTerm: string) => void;
  placeholder?: string;
}

function SearchInput({ search, placeholder }: SearchProps) {
  const [searchValue, setSearchValue] = useState<string>("");

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      search(value);
    }, 300),
    [search]
  );

  useEffect(() => {
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);

  const handleSearch = (e: React.SyntheticEvent) => {
    e.preventDefault();
    search(searchValue);
  };

  const handleClear = () => {
    setSearchValue("");
    search("");
  };

  return (
    <form
      onSubmit={handleSearch}
      className="w-full flex items-center bg-white border border-gray-300 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-shadow"
    >
      <img src={searchIcon} alt="search" className="w-5 h-5 opacity-70" />
      <input
        autoComplete="off"
        placeholder={placeholder || "Search..."}
        type="text"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleSearch(e);
          }
        }}
        className="flex-1 ml-3 bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none"
      />
      {searchValue && (
        <button
          type="button"
          onClick={handleClear}
          className="text-gray-400 hover:text-gray-600 ml-2 text-lg font-bold focus:outline-none"
        >
          ×
        </button>
      )}
    </form>
  );
}

// Utility function for debouncing with type annotations
function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return function (this: any, ...args: Parameters<T>) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), wait);
  };
}

export default SearchInput;
